import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  MapPin, Phone, Globe, Star, Edit2, Trash2, Save, X, Loader2,
  ThumbsUp, ThumbsDown, Minus, Calendar
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
  ContactActionRow,
  ClickablePhone,
  ClickableAddress 
} from '@/components/native/ContactActionButtons';

const CATEGORY_COLORS = {
  restaurant: { bg: 'bg-orange-100', text: 'text-orange-700' },
  mechanic: { bg: 'bg-blue-100', text: 'text-blue-700' },
  doctor: { bg: 'bg-green-100', text: 'text-green-700' },
  store: { bg: 'bg-purple-100', text: 'text-purple-700' },
  service: { bg: 'bg-amber-100', text: 'text-amber-700' },
  office: { bg: 'bg-slate-100', text: 'text-slate-700' },
  other: { bg: 'bg-pink-100', text: 'text-pink-700' },
};

const RATING_DISPLAY = {
  excellent: { icon: ThumbsUp, color: 'text-green-600', bg: 'bg-green-100', label: 'Excellent' },
  good: { icon: ThumbsUp, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Good' },
  neutral: { icon: Minus, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Neutral' },
  bad: { icon: ThumbsDown, color: 'text-red-600', bg: 'bg-red-100', label: 'Bad' },
};

const GO_BACK_DISPLAY = {
  yes: { color: 'bg-green-100 text-green-700', label: 'Yes, would return' },
  no: { color: 'bg-red-100 text-red-700', label: 'No, would not return' },
  maybe: { color: 'bg-amber-100 text-amber-700', label: 'Maybe' },
};

const PlaceDetailSheet = ({ 
  place, 
  open, 
  onOpenChange, 
  onPlaceUpdated, 
  onPlaceDeleted 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (place) {
      setFormData({
        name: place.name || '',
        category: place.category || 'other',
        address: place.address || '',
        phone: place.phone || '',
        website: place.website || '',
        notes: place.notes || '',
        would_go_back: place.would_go_back || null,
        experience_rating: place.experience_rating || null,
        experience_notes: place.experience_notes || '',
        recommended_services: place.recommended_services || '',
        last_visit_date: place.last_visit_date || '',
        is_favorite: place.is_favorite || false,
      });
    }
  }, [place]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Place name is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.put(`/places/${place.id}`, formData);
      setIsEditing(false);
      onPlaceUpdated();
    } catch (error) {
      toast.error('Failed to update place');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onPlaceDeleted();
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    if (place?.address) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.address)}`;
      window.open(url, '_blank');
    }
  };

  const handleCall = () => {
    if (place?.phone) {
      window.location.href = `tel:${place.phone}`;
    }
  };

  const handleOpenWebsite = () => {
    if (place?.website) {
      let url = place.website;
      if (!url.startsWith('http')) {
        url = `https://${url}`;
      }
      window.open(url, '_blank');
    }
  };

  if (!place) return null;

  const categoryColor = CATEGORY_COLORS[place.category] || CATEGORY_COLORS.other;
  const ratingDisplay = place.experience_rating ? RATING_DISPLAY[place.experience_rating] : null;
  const goBackDisplay = place.would_go_back ? GO_BACK_DISPLAY[place.would_go_back] : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Place Details</span>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-place-btn"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 hover:text-red-700"
                    data-testid="delete-place-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                    className="text-green-600"
                    data-testid="save-place-changes-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          </SheetTitle>
        </SheetHeader>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="my-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 mb-3">
              Are you sure you want to delete this place?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${categoryColor.bg}`}>
              <MapPin className={`h-7 w-7 ${categoryColor.text}`} />
            </div>
            <div className="flex-1">
              {isEditing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="text-lg font-semibold"
                />
              ) : (
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  {place.name}
                  {place.is_favorite && (
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  )}
                </h3>
              )}
              <Badge className={`${categoryColor.bg} ${categoryColor.text} mt-1`}>
                {isEditing ? (
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="bg-transparent border-none text-xs"
                  >
                    <option value="restaurant">Restaurant</option>
                    <option value="mechanic">Mechanic</option>
                    <option value="doctor">Doctor</option>
                    <option value="store">Store</option>
                    <option value="service">Service</option>
                    <option value="office">Office</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  place.category
                )}
              </Badge>
            </div>
          </div>

          {/* Quick Actions using Native Integrations */}
          {!isEditing && (
            <ContactActionRow
              phone={place.phone}
              address={place.address}
              businessName={place.name}
              className="pb-2"
              data-testid="place-action-row"
            />
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            {/* Address */}
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Address</Label>
                {isEditing ? (
                  <Textarea
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Address"
                    rows={2}
                    data-testid="edit-place-address-input"
                  />
                ) : place.address ? (
                  <ClickableAddress 
                    address={place.address}
                    businessName={place.name}
                    showIcon={false}
                    className="text-sm"
                    data-testid="clickable-place-address"
                  />
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Phone</Label>
                {isEditing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Phone number"
                    data-testid="edit-place-phone-input"
                  />
                ) : place.phone ? (
                  <ClickablePhone 
                    phone={place.phone}
                    showIcon={false}
                    className="text-sm"
                    data-testid="clickable-place-phone"
                  />
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Website */}
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Website</Label>
                {isEditing ? (
                  <Input
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="Website URL"
                  />
                ) : place.website ? (
                  <button
                    onClick={handleOpenWebsite}
                    className="text-sm text-blue-600 hover:underline text-left"
                  >
                    {place.website}
                  </button>
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>
          </div>

          {/* Experience Section */}
          <div className="pt-4 border-t border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-3">Your Experience</h4>
            
            {isEditing ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Would you go back?</Label>
                  <select
                    value={formData.would_go_back || ''}
                    onChange={(e) => handleChange('would_go_back', e.target.value || null)}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="maybe">Maybe</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Experience Rating</Label>
                  <select
                    value={formData.experience_rating || ''}
                    onChange={(e) => handleChange('experience_rating', e.target.value || null)}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="neutral">Neutral</option>
                    <option value="bad">Bad</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Experience Notes</Label>
                  <Textarea
                    value={formData.experience_notes}
                    onChange={(e) => handleChange('experience_notes', e.target.value)}
                    placeholder="Fast service, honest pricing..."
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Recommended Services/Items</Label>
                  <Textarea
                    value={formData.recommended_services}
                    onChange={(e) => handleChange('recommended_services', e.target.value)}
                    placeholder="Oil changes, tire rotations..."
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Last Visit Date</Label>
                  <Input
                    type="date"
                    value={formData.last_visit_date}
                    onChange={(e) => handleChange('last_visit_date', e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4 space-y-3">
                  {/* Would Go Back & Rating */}
                  <div className="flex flex-wrap gap-2">
                    {goBackDisplay && (
                      <Badge className={goBackDisplay.color}>
                        {goBackDisplay.label}
                      </Badge>
                    )}
                    {ratingDisplay && (
                      <Badge className={`${ratingDisplay.bg} ${ratingDisplay.color}`}>
                        <ratingDisplay.icon className="h-3 w-3 mr-1" />
                        {ratingDisplay.label}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Experience Notes */}
                  {place.experience_notes && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Notes</p>
                      <p className="text-sm text-slate-700">{place.experience_notes}</p>
                    </div>
                  )}
                  
                  {/* Recommended */}
                  {place.recommended_services && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Recommended</p>
                      <p className="text-sm text-slate-700">{place.recommended_services}</p>
                    </div>
                  )}
                  
                  {/* Last Visit */}
                  {place.last_visit_date && (
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      Last visit: {format(parseISO(place.last_visit_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  
                  {/* Empty State */}
                  {!goBackDisplay && !ratingDisplay && !place.experience_notes && !place.recommended_services && !place.last_visit_date && (
                    <p className="text-sm text-slate-400 text-center py-2">
                      Ready to add your experience — tap edit above to share your thoughts.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Notes */}
          {(isEditing || place.notes) && (
            <div className="pt-4 border-t border-slate-100">
              <Label className="text-xs text-slate-500">General Notes</Label>
              {isEditing ? (
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add notes"
                  rows={3}
                  className="mt-1"
                />
              ) : (
                <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">
                  {place.notes}
                </p>
              )}
            </div>
          )}

          {/* Favorite toggle in edit mode */}
          {isEditing && (
            <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
              <input
                type="checkbox"
                id="edit_place_favorite"
                checked={formData.is_favorite}
                onChange={(e) => handleChange('is_favorite', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="edit_place_favorite" className="cursor-pointer">
                Mark as favorite
              </Label>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PlaceDetailSheet;
